import React, { useState, useEffect, SyntheticEvent } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Stack,
  CardContent,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  Zoom
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

interface Member {
  name: string;
  score?: number;
  icon?: string;
}

interface Members {
  [key: string]: Member | null;
}

interface ScoreService {
  getScore: (memberId: string | null) => Promise<number>;
  saveScore: (score: number, memberId: string | null) => Promise<number>;
}

interface ScoresProps {
  scoreService: ScoreService;
  currentMemberId: string | null;
  onMemberChange: (memberId: string) => void;
  members: Members;
  loadAllMembers: () => Promise<Members>;
}

const Scores: React.FC<ScoresProps> = ({ 
  scoreService,
  currentMemberId,
  onMemberChange,
  members,
  loadAllMembers
}) => {
  const [score, setScore] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeMemberTab, setActiveMemberTab] = useState<number>(0);
  const [memberName, setMemberName] = useState<string>('');
  const [scoreMode, setScoreMode] = useState<'total' | 'relative'>('total');
  const [showLevelUpAnimation, setShowLevelUpAnimation] = useState<boolean>(false);
  const [newLevel, setNewLevel] = useState<number>(1);

  // Helper function to get emoji icon
  const getEmojiIcon = (icon?: string) => {
    return icon || '😊';
  };

  // Helper function to calculate relative score (points needed to next level)
  const getRelativeScore = (totalScore: number) => {
    if (totalScore % 100 === 0 && totalScore > 0) {
      return 0; // Already at a level milestone
    }
    const nextLevel = Math.ceil(totalScore / 100) * 100;
    return nextLevel - totalScore;
  };

  // Helper function to get current level
  const getCurrentLevel = (totalScore: number) => {
    return Math.floor(totalScore / 100);
  };


  // Get display score based on mode
  const getDisplayScore = () => {
    if (scoreMode === 'relative') {
      const relativeScore = getRelativeScore(score);
      return relativeScore === 0 ? 0 : -relativeScore;
    }
    return score;
  };

  // Handle score mode toggle
  const handleScoreModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'total' | 'relative') => {
    if (newMode !== null) {
      setScoreMode(newMode);
    }
  };

  // Check if level up occurred and trigger animation
  const checkLevelUp = (oldScore: number, newScore: number) => {
    const oldLevel = getCurrentLevel(oldScore);
    const newCurrentLevel = getCurrentLevel(newScore);
    
    if (newCurrentLevel > oldLevel && newScore >= 100) {
      setNewLevel(newCurrentLevel);
      setShowLevelUpAnimation(true);
      
      // Hide animation after 3 seconds
      setTimeout(() => {
        setShowLevelUpAnimation(false);
      }, 3000);
    }
  };

  // Load score when current member changes
  useEffect(() => {
    const loadMemberScore = async () => {
      if (currentMemberId && members[currentMemberId]) {
        setLoading(true);
        try {
          const memberScore = await scoreService.getScore(currentMemberId);
          setScore(memberScore);
          setMemberName(members[currentMemberId].name);
        } catch (error) {
          console.error('Error loading member score:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadMemberScore();
  }, [currentMemberId, members, scoreService]);

  // Sync member tab index with current member
  useEffect(() => {
    if (currentMemberId && members) {
      const memberIds = Object.keys(members).filter(id => members[id] !== null);
      const currentIndex = memberIds.indexOf(currentMemberId);
      if (currentIndex >= 0 && currentIndex !== activeMemberTab) {
        setActiveMemberTab(currentIndex);
      }
    }
  }, [currentMemberId, members, activeMemberTab]);

  // Load members when component mounts
  useEffect(() => {
    if (loadAllMembers) {
      loadAllMembers();
    }
  }, [loadAllMembers]);

  // Handle member tab change
  const handleMemberTabChange = (_event: SyntheticEvent, newValue: number) => {
    setActiveMemberTab(newValue);
    
    // Get the member ID from the members object
    const memberIds = Object.keys(members).filter(id => members[id] !== null);
    if (memberIds[newValue] && onMemberChange) {
      onMemberChange(memberIds[newValue]);
    }
  };

  // Update score handlers
  const incrementScore = async () => {
    if (!currentMemberId) {
      console.warn('No member selected, cannot update score');
      return;
    }

    const oldScore = score;
    const newScore = score + 1;
    setScore(newScore);
    
    // Check for level up
    checkLevelUp(oldScore, newScore);
    
    try {
      await scoreService.saveScore(newScore, currentMemberId);
    } catch (error) {
      console.error('Error saving score:', error);
    }
  };
  
  const decrementScore = async () => {
    if (!currentMemberId) {
      console.warn('No member selected, cannot update score');
      return;
    }

    const newScore = score - 1;
    setScore(newScore);
    
    try {
      await scoreService.saveScore(newScore, currentMemberId);
    } catch (error) {
      console.error('Error saving score:', error);
    }
  };

  const memberIds = Object.keys(members).filter(id => members[id] !== null);
  
  if (memberIds.length === 0) {
    return (
      <Paper elevation={1}>
        <CardContent>
          <Alert severity="info">
            No family members found. Please add members first.
          </Alert>
        </CardContent>
      </Paper>
    );
  }
  
  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
        `}
      </style>
      <Paper elevation={1}>
        <CardContent>
        {/* Member tabs */}
        <Paper>
          <Tabs 
            value={activeMemberTab} 
            onChange={handleMemberTabChange}
            centered
          >
            {memberIds.map((memberId, index) => (
              <Tab 
                key={memberId} 
                label={members[memberId]?.name || `Member ${index + 1}`}
              />
            ))}
          </Tabs>
        </Paper>
        
        {/* Level Up Animation */}
        <Fade in={showLevelUpAnimation} timeout={500}>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              pointerEvents: showLevelUpAnimation ? 'auto' : 'none'
            }}
          >
            <Zoom in={showLevelUpAnimation} timeout={800}>
              <Box textAlign="center">
                <Typography
                  variant="h1"
                  sx={{
                    color: '#ffd700',
                    fontWeight: 'bold',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                    mb: 2,
                    animation: 'pulse 1s infinite'
                  }}
                >
                  🎉 LEVEL UP! 🎉
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                  }}
                >
                  Level {newLevel}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#ffd700',
                    mt: 2,
                    fontWeight: 'bold'
                  }}
                >
                  Great job, {memberName}! 🌟
                </Typography>
              </Box>
            </Zoom>
          </Box>
        </Fade>

        {/* Score content for selected member */}
        <Box paddingTop={3}>
          {currentMemberId && members[currentMemberId] && (
            <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', fontSize: '2rem', width: 64, height: 64 }}>
                {getEmojiIcon(members[currentMemberId]?.icon)}
              </Avatar>
              <Typography variant="h2" gutterBottom>
                {memberName}
              </Typography>
            </Stack>
          )}
          
          {loading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Score Mode Toggle */}
              {currentMemberId && (
                <Box display="flex" justifyContent="center" sx={{ mb: 3 }}>
                  <ToggleButtonGroup
                    value={scoreMode}
                    exclusive
                    onChange={handleScoreModeChange}
                    aria-label="score mode"
                    size="small"
                  >
                    <ToggleButton value="total" aria-label="total score">
                      Total
                    </ToggleButton>
                    <ToggleButton value="relative" aria-label="relative score">
                      Relative
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
              
              <Stack spacing={4}>
                {!currentMemberId ? (
                  <Alert severity="info">
                    <Box textAlign="center">
                      Please select a family member to track scores
                    </Box>
                  </Alert>
                ) : (
                  <Box textAlign="center">
                    <Typography variant="h1" component="div" fontWeight="bold">
                      {getDisplayScore()}
                    </Typography>
                    
                    {/* Additional info based on mode */}
                    {scoreMode === 'total' ? (
                      <Typography variant="h6" color="textSecondary" sx={{ mt: 1 }}>
                        (Level {getCurrentLevel(score)})
                      </Typography>
                    ) : (
                      <Typography variant="h6" color="textSecondary" sx={{ mt: 1 }}>
                        (Level {getCurrentLevel(score)} | Total Score: {score})
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>
              
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button 
                  variant="contained" 
                  color="error" 
                  onClick={decrementScore}
                  startIcon={<RemoveIcon />}
                  disabled={!currentMemberId}
                  sx={{ minWidth: 120 }}
                >
                  Remove
                </Button>
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={incrementScore}
                  startIcon={<AddIcon />}
                  disabled={!currentMemberId}
                  sx={{ minWidth: 120 }}
                >
                  Add
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </CardContent>
    </Paper>
    </>
  );
};

export default Scores;